<template>
  <Content
    class="route-builder phase-1 no-scroll"
    header-icon="map-marked-alt"
    :header-label="t('routeBuilder.routeBuilder')"
    :loading="isAnyLoading"
    :class="{
      blocked: !selectedClient,
      'has-error': currentStep === 'validation_error',
    }"
    full-width
  >
    <Splitter
      ref="verticalSplitter"
      layout="vertical"
      :gutter-size="SPLITTER_GUTTER_SIZE"
      @resizeend="onManualVerticalResize"
    >
      <SplitterPanel
        :size="panelSizes.height.firstRow"
        class="view-route-panel"
      >
        <ViewRouteSection
          id="view-route"
          v-model="viewRouteSectionSize"
          :proposed-shipments="proposedShipments"
          :mileages="mileages"
          @collapse="onViewRouteSectionCollapse"
          @expand="onViewRouteSectionExpand"
          @reset="onViewRouteSectionReset"
        />
      </SplitterPanel>

      <SplitterPanel :size="panelSizes.height.secondRow">
        <Transition name="initial-bottom-row">
          <Splitter v-if="isOnPlanningStep" :gutter-size="SPLITTER_GUTTER_SIZE">
            <SplitterPanel
              :size="panelSizes.width.firstColumn"
              class="planning-pool-panel"
            >
              <PlanningPoolSection
                id="planning-pool"
                :ship-orders="planningPool"
                :proposed-shipments="proposedShipments"
                :additional-ship-orders="additionalShipOrders"
                :ship-order-errors="shipOrderErrors"
                @add-to-planning-pool="addShipOrderToPlanningPool"
                @add-to-shipment="addShipOrderToShipment"
                @create-shipment="createShipmentFromShipOrders"
                @remove-from-planning-pool="removeShipOrdersFromPlanningPool"
                @remove-from-shipment="removeShipOrderFromShipment"
                @scaffold="planShipOrders"
                @split-ship-order="splitShipOrder"
              />
            </SplitterPanel>

            <SplitterPanel
              :size="panelSizes.width.secondColumn"
              class="shipments-panel"
            >
              <ProposedShipmentSection
                id="shipments"
                ref="proposedShipmentSection"
                :header="t('shipments.shipments', 2)"
                :shipments="proposedShipments"
                :mileages="mileages"
                :disabled="currentStep === 'validation_error'"
                @add-shipment="onAddProposedShipment"
                @add-stop="onAddStopToShipment"
                @clear="clearProposedShipments"
                @rate="onRateProposedShipments"
                @rearrange-stops="onRearrangeStops"
                @remove-ship-order="removeShipOrderFromShipment"
                @remove-shipment="onRemoveProposedShipment"
                @remove-stop="removeStopFromShipment"
                @submit="onCreateProposedShipments"
              />
            </SplitterPanel>
          </Splitter>
        </Transition>

        <Transition name="rate-shop-transition">
          <RouteBuilderRateShopSection
            v-if="currentStep === 'rating'"
            id="rate-shop"
            :proposed-shipments="proposedShipments"
            @back="onBackToPlanning"
            @select-group="selectRateGroup"
          />
        </Transition>

        <RouteBuilderExecutedShipmentsSection
          v-if="currentStep === 'executing' && selectedRateGroup"
          id="execution"
          :type="selectedRateGroup.type"
          :rate-quotes="selectedRateGroup.rates"
          :rate-selection-reason="selectedRateGroup.rateSelectionReason"
          @back="
            selectedRateGroup.type === 'none'
              ? onBackToPlanning
              : onBackToRating
          "
        />
      </SplitterPanel>
    </Splitter>

    <AddShipmentModal
      v-model:visible="isAddShipmentModalVisible"
      @confirm="onAddProposedShipmentConfirm"
    />

    <RearrangeStopsModal
      v-model:visible="rearrangeStopsModal.visible"
      :shipment="rearrangeStopsModal.shipment"
      @add-stop="onAddStopToShipment"
      @confirm="rearrangeStops"
    />

    <AddStopToShipmentModal
      v-model:visible="addStopModal.visible"
      :stop-type="addStopModal.stopType"
      :stop-selector="addStopModal.stopSelector"
      @confirm="onAddStopToShipmentConfirm"
    />

    <ChangeDomainModal
      v-model:visible="isChangeDomainModalVisible"
      :selected-domain="selectedClient"
      :selectable-domain-types="[DomainType.Client]"
      :domain-options="clients.data"
      :header="t('common.selectParam', { param: t('clients.clients') })"
      position="center"
      closable
      @change-domain="onClientChange"
    >
      <template v-if="!!selectedClient" #message>
        <Message severity="warn" icon="pi pi-exclamation-triangle">
          {{
            t('common.selectingClientWillResetParam', {
              param: t('routeBuilder.routePlan'),
            })
          }}
        </Message>
      </template>
    </ChangeDomainModal>
  </Content>
</template>

<script lang="ts" setup>
import { usePrimeVue } from 'primevue/config';
import type { PropType, Ref } from 'vue';
import {
  computed,
  h,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  toRefs,
  useTemplateRef,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { Environment } from '@rygen/rygen-ui/env/Environment';
import { EnvironmentPropertiesLoader } from '@rygen/rygen-ui/env/EnvironmentPropertiesLoader';
import {
  DomainReference,
  DomainTree,
  DomainType,
  Optional,
} from '@rygen/rygen-ui/model';

import {
  useRouteBuilder,
  useShipOrderValidations,
} from '@/composables/routeBuilder';
import { ShipmentStopType } from '@/model';
import {
  RequestAddStopToShipmentPayload,
  ProposedShipment,
  RearrangeStopsPayload,
  RemoveShipmentPayload,
  StopSelector,
  ProposedShipmentStop,
  AddStopPayload,
  RouteBuilderRequest,
  ProposedShipmentRateQuote,
} from '@/model/routeBuilder';
import { useClientStore } from '@/stores/Client';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { useMessageStore } from '@/stores/Message';
import { useParentEntityStore } from '@/stores/ParentEntity';

import RearrangeStopsModal from '@/components/multistop/RearrangeStopsModal.vue';
import AddShipmentModal from '@/components/routeBuilder/AddShipmentModal.vue';
import AddStopToShipmentModal from '@/components/routeBuilder/addStopToShipment/AddStopToShipmentModal.vue';
import PlanningPoolSection from '@/components/routeBuilder/planningPool/PlanningPoolSection.vue';
import ProposedShipmentSection from '@/components/routeBuilder/proposedShipment/ProposedShipmentSection.vue';
import RouteBuilderRateShopSection from '@/components/routeBuilder/rateShop/RouteBuilderRateShopSection.vue';
import RouteBuilderExecutedShipmentsSection from '@/components/routeBuilder/RouteBuilderExecutedShipmentsSection.vue';
import ViewRouteSection, {
  ViewRouteSectionSize,
} from '@/components/routeBuilder/ViewRouteSection.vue';
import ClientAvatar from '@rygen/rygen-ui/components/client/ClientAvatar.vue';
import Content from '@rygen/rygen-ui/components/content/Content.vue';
import ChangeDomainModal from '@rygen/rygen-ui/components/domain/ChangeDomainModal.vue';
import Message from 'primevue/message';
import Splitter, { SplitterResizeEndEvent } from 'primevue/splitter';
import SplitterPanel from 'primevue/splitterpanel';

const ROUTE_BUILDER_PHASE_MESSAGE_KEY = 'route-builder-phase';
const OWNING_CLIENT_MESSAGE_KEY = 'route-builder-owning-client-message';

const VIEW_ROUTE_SECTION_DEFAULT_SIZES: Readonly<
  Record<ViewRouteSectionSize, number>
> = Object.freeze({
  collapsed: 15,
  normal: 33,
  expanded: 100,
});

/** Size of the `Splitter`'s divider in pixels */
const SPLITTER_GUTTER_SIZE = 2;

const props = defineProps({
  shipmentIds: {
    type: Array as PropType<number[]>,
    default: () => [],
  },
  orderIds: {
    type: Array as PropType<number[]>,
    default: () => [],
  },
});
const { shipmentIds, orderIds } = toRefs(props);

const proposedShipmentSection = useTemplateRef('proposedShipmentSection');
const verticalSplitter = useTemplateRef('verticalSplitter');

const { clientTreeMap, clients, loadClients } = useClientStore();
const { selectedDomain } = useCurrentUserStore();
const { t } = useI18n();
const { addMessage, removeMessage } = useMessageStore();
const { clearParentEntity, setParentEntity } = useParentEntityStore();
const {
  addProposedShipment,
  addShipOrderToPlanningPool,
  addShipOrderToShipment,
  addStopToShipment,
  additionalShipOrders,
  clearProposedShipments,
  createShipmentFromShipOrders,
  currentStep,
  findProposedShipment,
  isOnPlanningStep,
  loadRelatedShipOrdersForOrders,
  loadRelatedShipOrdersForShipments,
  mileages,
  planShipOrders,
  planningPool,
  planningPoolShipOrders,
  proposedShipments,
  rearrangeStops,
  removeShipOrderFromShipment,
  removeShipOrdersFromPlanningPool,
  removeShipment,
  removeStopFromShipment,
  request,
  reset,
  selectRateGroup,
  selectedRateGroup,
  splitShipOrder,
} = useRouteBuilder();
const router = useRouter();
const { errors: shipOrderErrors, validate: validateShipOrders } =
  useShipOrderValidations(planningPoolShipOrders, proposedShipments);

const selectedClient: Ref<DomainTree | undefined> = ref();
const isChangeDomainModalVisible = ref(false);

const viewRouteSectionSize: Ref<ViewRouteSectionSize> = ref('normal');
const isAddShipmentModalVisible = ref(false);

const rearrangeStopsModal = ref({
  visible: false,
  shipment: new ProposedShipment(),
});

const addStopModal = ref({
  visible: false,
  shipment: new ProposedShipment(),
  stopType: undefined as Optional<ShipmentStopType>,
  stopSelector: undefined as Optional<StopSelector>,
  sequenceNumber: undefined as Optional<number>,
});

const panelSizes = ref({
  height: {
    firstRow: VIEW_ROUTE_SECTION_DEFAULT_SIZES.normal,
    secondRow: 100 - VIEW_ROUTE_SECTION_DEFAULT_SIZES.normal,
  },
  width: {
    firstColumn: 50,
    secondColumn: 50,
  },
});

const isLocalOrDev = computed(() => {
  const effectiveEnvironment =
    EnvironmentPropertiesLoader.getEffectiveEnvironment(
      window.location.hostname,
    );

  return (
    effectiveEnvironment === Environment.LOCAL ||
    effectiveEnvironment === Environment.DEV
  );
});

const isAnyLoading = computed(() => {
  return planningPool.value.loading || additionalShipOrders.value.loading;
});

const onAddStopToShipment = ({
  stopType,
  stopSelector,
  sequenceNumber,
  shipmentIdentifier,
}: RequestAddStopToShipmentPayload) => {
  const { proposedShipment } = findProposedShipment(shipmentIdentifier);

  addStopModal.value.shipment = proposedShipment;
  addStopModal.value.stopType = stopType;
  addStopModal.value.stopSelector = stopSelector;
  addStopModal.value.sequenceNumber = sequenceNumber;
  addStopModal.value.visible = true;
};

const onAddStopToShipmentConfirm = (payload: AddStopPayload) => {
  const shipmentIdentifier = addStopModal.value.shipment.identifier;
  const sequenceNumber = addStopModal.value.sequenceNumber;
  addStopToShipment({
    ...payload,
    sequenceNumber,
    shipmentIdentifier,
  });
};

const onRearrangeStops = ({ shipmentIdentifier }: RearrangeStopsPayload) => {
  const { proposedShipment } = findProposedShipment(shipmentIdentifier);
  rearrangeStopsModal.value.shipment = proposedShipment;
  rearrangeStopsModal.value.visible = true;
};

const onRemoveProposedShipment = ({
  shipmentIdentifiers,
}: RemoveShipmentPayload) => {
  shipmentIdentifiers.forEach((identifier) => removeShipment(identifier));
};

const onViewRouteSectionCollapse = () => {
  updateRowSizes(VIEW_ROUTE_SECTION_DEFAULT_SIZES.collapsed);
};

const onViewRouteSectionExpand = () => {
  updateRowSizes(VIEW_ROUTE_SECTION_DEFAULT_SIZES.expanded);
};

const onViewRouteSectionReset = () => {
  updateRowSizes(VIEW_ROUTE_SECTION_DEFAULT_SIZES.normal);
};

const updateRowSizes = (newFirstRowHeight: number) => {
  panelSizes.value.height.firstRow = newFirstRowHeight;
  panelSizes.value.height.secondRow = 100 - newFirstRowHeight;

  nextTick(() => verticalSplitter.value?.resetState());
};

const isWithinThreshold = (size: ViewRouteSectionSize, actualSize: number) => {
  const sizeAmount = VIEW_ROUTE_SECTION_DEFAULT_SIZES[size];
  const minAmount = sizeAmount - 10;
  const maxAmount = sizeAmount + 10;

  return actualSize >= minAmount && actualSize <= maxAmount;
};

const onManualVerticalResize = ({
  sizes: [firstRowSize],
}: SplitterResizeEndEvent) => {
  if (isWithinThreshold('collapsed', firstRowSize)) {
    viewRouteSectionSize.value = 'collapsed';
  } else if (isWithinThreshold('expanded', firstRowSize)) {
    viewRouteSectionSize.value = 'expanded';
  } else {
    viewRouteSectionSize.value = 'normal';
  }

  nextTick(() => updateRowSizes(firstRowSize));
};

const onAddProposedShipment = () => {
  isAddShipmentModalVisible.value = true;
};

const onAddProposedShipmentConfirm = (stops: ProposedShipmentStop[]) => {
  addProposedShipment(stops, selectedClient.value?.asDomainReference());
};

const validateProposedShipments = async (): Promise<boolean> => {
  const areProposedShipmentsValid =
    await proposedShipmentSection.value?.validate();
  return areProposedShipmentsValid ?? false;
};

const validateManualConditions = async (): Promise<boolean> => {
  // Don't chain these calls, `one && two`, so a failure in one doesn't prevent
  // successive validations from running
  const areShipOrdersValid = await validateShipOrders();
  const areProposedShipmentsValid = await validateProposedShipments();
  return areShipOrdersValid && areProposedShipmentsValid;
};

const onCreateProposedShipments = async () => {
  const areManualValidationsValid = await validateManualConditions();
  if (!areManualValidationsValid) {
    currentStep.value = 'validation_error';
    return;
  }

  const rates = proposedShipments.value.map(
    (s) => new ProposedShipmentRateQuote({ proposedShipment: s }),
  );
  selectedRateGroup.value = {
    rates,
    type: 'none',
  };
  currentStep.value = 'executing';
};

const onRateProposedShipments = async () => {
  // const areAutoValidationsValid = await validate();
  const areManualValidationsValid = await validateManualConditions();
  currentStep.value = areManualValidationsValid ? 'rating' : 'validation_error';
};

const onBackToPlanning = () => {
  selectedRateGroup.value = undefined;
  currentStep.value = 'planning';
};

const onBackToRating = () => {
  selectedRateGroup.value = undefined;
  currentStep.value = 'rating';
};

const onClientChange = async (newClient: DomainReference) => {
  selectedClient.value = newClient.id
    ? clientTreeMap.value.get(newClient.id)
    : undefined;
  reset();

  // Clear the orderId/shipmentId query params
  const query = { ...router.currentRoute.value.query };
  delete query.orderId;
  delete query.shipmentId;

  await router.replace({ query });
};

const updateSelectedClient = (currentDomain: DomainReference | undefined) => {
  // Client should default to owner of selected orders/shipments
  let clientId = planningPoolShipOrders.value[0]?.owner?.id;

  // Fallback to the selected domain if its a client
  if (!clientId && currentDomain?.domainType === DomainType.Client) {
    clientId = currentDomain.id;
  }

  selectedClient.value = clientId
    ? clientTreeMap.value.get(clientId)
    : undefined;

  // Otherwise, make the user select a client
  isChangeDomainModalVisible.value = !selectedClient.value;
};

watch(orderIds, async (newVal: number[]) => {
  if (newVal.length) {
    loadRelatedShipOrdersForOrders(planningPool.value, newVal);
  }
});

watch(shipmentIds, async (newVal: number[]) => {
  if (newVal.length) {
    loadRelatedShipOrdersForShipments(planningPool.value, newVal);
  }
});

watch(
  planningPoolShipOrders,
  () => {
    if (currentStep.value === 'validation_error') {
      currentStep.value = 'planning';
      shipOrderErrors.value = {};
    }
  },
  { deep: true },
);

watch(
  proposedShipments,
  () => {
    if (currentStep.value === 'validation_error') {
      currentStep.value = 'planning';
    }
  },
  { deep: true },
);

watch(selectedDomain, (newVal: DomainReference | undefined) => {
  updateSelectedClient(newVal);
});

watch(
  selectedClient,
  (newVal: DomainTree | undefined) => {
    if (!newVal) {
      return;
    }

    request.value = new RouteBuilderRequest({
      owner: newVal.asDomainReference(),
    });
    setParentEntity(request);

    if (isLocalOrDev.value) {
      addMessage({
        id: ROUTE_BUILDER_PHASE_MESSAGE_KEY,
        severity: 'info',
        text: 'Route Builder Phase 1',
      });
    }

    addMessage({
      id: OWNING_CLIENT_MESSAGE_KEY,
      severity: 'info',
      text: t('common.assignedToParam', { param: newVal.name }),
      iconSlot: () =>
        h(ClientAvatar, {
          name: newVal.name,
          logo: newVal.branding?.avatar?.signedUrl,
        }),
      onClick: () => (isChangeDomainModalVisible.value = true),
    });
  },
  { immediate: true },
);

watch(isChangeDomainModalVisible, (newVal: boolean, oldVal: boolean) => {
  if (oldVal && !newVal && !selectedClient.value) {
    router.back();
  }
});

onMounted(async () => {
  const initializationPromises = [];
  if (orderIds.value.length) {
    initializationPromises.push(
      loadRelatedShipOrdersForOrders(planningPool.value, orderIds.value),
    );
  } else if (shipmentIds.value.length) {
    initializationPromises.push(
      loadRelatedShipOrdersForShipments(planningPool.value, shipmentIds.value),
    );
  }

  initializationPromises.push(loadClients());
  await Promise.allSettled(initializationPromises);
  updateSelectedClient(selectedDomain.value);
});

onBeforeUnmount(() => {
  clearParentEntity();
  removeMessage(ROUTE_BUILDER_PHASE_MESSAGE_KEY);
  removeMessage(OWNING_CLIENT_MESSAGE_KEY);
});

defineExpose({
  $primevue: usePrimeVue(),
});
</script>
